import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaDollarSign, FaFileAlt, FaEdit, FaTrash, FaArrowLeft,
  FaFileInvoiceDollar, FaUserTag, FaClipboardList, FaEyeSlash, FaCalendarAlt,
} from 'react-icons/fa';
import { getServiceById, deleteService } from './serviceService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import FeedbackModal from '../../components/shared/ModalNotification';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const Page = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding-bottom: ${p => p.theme.spacing(4)};
`;

// Name + price together, so the two things you actually came for are the first
// thing you see. The old page put the name in a header AND repeated it as a
// "Service Name" field below.
const Hero = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.inputGray}22;
  border-radius: 14px;
  padding: ${p => p.theme.spacing(3)};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(3)};
  margin-bottom: ${p => p.theme.spacing(2)};

  @media (max-width: 640px) {
    flex-direction: column;
    gap: ${p => p.theme.spacing(2)};
  }
`;

const Title = styled.div`
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 1.5rem;
    color: ${p => p.theme.colors.textPrimary};
    word-break: break-word;
    line-height: 1.25;
  }
`;

const Tags = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px;
`;

const Tag = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  padding: 3px 9px; border-radius: 10px;
  background: ${p => (p.$muted ? p.theme.colors.inputGray : p.theme.colors.success)}22;
  color: ${p => (p.$muted ? p.theme.colors.textSecondary : p.theme.colors.success)};
  border: 1px solid ${p => (p.$muted ? p.theme.colors.inputGray : p.theme.colors.success)}44;
`;

const PriceBox = styled.div`
  flex-shrink: 0;
  text-align: right;

  .amount {
    font-size: 1.9rem;
    font-weight: 800;
    color: ${p => p.theme.colors.primary};
    line-height: 1;
    white-space: nowrap;
  }
  .cap {
    display: block;
    font-size: 0.7rem;
    color: ${p => p.theme.colors.textSecondary};
    margin-top: 5px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  @media (max-width: 640px) { text-align: left; }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: ${p => p.theme.spacing(2)};
`;

const Stat = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.inputGray}22;
  border-radius: 12px;
  padding: 14px 16px;

  .n {
    font-size: 1.35rem; font-weight: 700;
    color: ${p => p.theme.colors.textPrimary};
    line-height: 1;
  }
  .l {
    display: flex; align-items: center; gap: 6px;
    font-size: 0.75rem; color: ${p => p.theme.colors.textSecondary}; margin-top: 7px;
  }
`;

const Section = styled.section`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.inputGray}22;
  border-radius: 14px;
  padding: ${p => p.theme.spacing(3)};
  margin-bottom: ${p => p.theme.spacing(2)};

  h2 {
    display: flex; align-items: center; gap: 9px;
    margin: 0 0 ${p => p.theme.spacing(2)};
    font-size: 0.78rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    color: ${p => p.theme.colors.textSecondary};
  }

  /* pre-wrap matters: 20 of 44 descriptions contain real line breaks, and the
     old page rendered them without it, collapsing paragraphs into one block. */
  p {
    margin: 0;
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.94rem;
    line-height: 1.65;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .empty { color: ${p => p.theme.colors.textSecondary}; font-style: italic; }
`;

const Meta = styled.div`
  display: flex; flex-wrap: wrap; gap: 18px;
  font-size: 0.76rem; color: ${p => p.theme.colors.textSecondary};
  padding: 0 ${p => p.theme.spacing(1)};

  span { display: inline-flex; align-items: center; gap: 6px; }
`;

const Bar = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
  margin-bottom: ${p => p.theme.spacing(2)};

  @media (max-width: 640px) { > button { flex: 1; justify-content: center; } }
`;

const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 16px; border-radius: 10px; cursor: pointer;
  font-size: 0.85rem; font-weight: 600;
  background: ${p => (p.$variant === 'primary' ? p.theme.colors.primary : 'transparent')};
  color: ${p => {
    if (p.$variant === 'primary') return '#000';
    if (p.$variant === 'danger') return p.theme.colors.error;
    return p.theme.colors.textSecondary;
  }};
  border: 1px solid ${p => {
    if (p.$variant === 'primary') return p.theme.colors.primary;
    if (p.$variant === 'danger') return p.theme.colors.error + '66';
    return p.theme.colors.inputGray + '44';
  }};

  &:hover {
    border-color: ${p => (p.$variant === 'danger' ? p.theme.colors.error : p.theme.colors.primary)};
    color: ${p => {
      if (p.$variant === 'primary') return '#000';
      return p.$variant === 'danger' ? p.theme.colors.error : p.theme.colors.primary;
    }};
  }
`;

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';
};

const shortDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function ServiceDetails() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [redirectAfterFeedback, setRedirectAfterFeedback] = useState(false);
  const navigate = useNavigate();

  const showNotification = (message, isSuccess = true) => {
    setFeedbackMessage(message);
    setFeedbackSuccess(isSuccess);
    setShowFeedback(true);
  };

  useEffect(() => {
    const fetchService = async () => {
      try {
        setLoading(true);
        const response = await getServiceById(id);
        setService(response.data);
      } catch (error) {
        console.error('Error fetching service details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  const handleDelete = async () => {
    try {
      const response = await deleteService(id);
      const msg = response?.data?.message || 'Service deleted successfully.';
      setDeleteModalOpen(false);
      setRedirectAfterFeedback(true);
      showNotification(msg, true);
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Failed to delete service.';
      console.error('Error deleting service:', error);
      setDeleteModalOpen(false);
      showNotification(msg, false);
    }
  };

  if (loading) return (
    <DashboardLayout><Page><LoadingSpinner /></Page></DashboardLayout>
  );

  if (!service) return (
    <DashboardLayout><Page><Section><p className="empty">Service not found.</p></Section></Page></DashboardLayout>
  );

  const usage = service.usage || {};
  const inUse = (usage.estimateItems || 0) + (usage.leadRequests || 0) + (usage.changeOrderItems || 0) > 0;
  const inactive = service.isActive === false;

  return (
    <DashboardLayout>
      <Page>
        <Bar>
          <Btn onClick={() => navigate('/services')}><FaArrowLeft /> Back</Btn>
          <Btn $variant="primary" onClick={() => navigate(`/services/edit/${service.id}`)}><FaEdit /> Edit</Btn>
          <Btn $variant="danger" onClick={() => setDeleteModalOpen(true)}><FaTrash /> Delete</Btn>
        </Bar>

        <Hero>
          <Title>
            <h1>{service.service_name}</h1>
            <Tags>
              {inactive
                ? <Tag $muted><FaEyeSlash size={9} /> Inactive</Tag>
                : <Tag>Active</Tag>}
              {inUse && <Tag $muted>In use</Tag>}
            </Tags>
          </Title>
          <PriceBox>
            <div className="amount"><FaDollarSign size={20} style={{ verticalAlign: 'baseline' }} />{money(service.price_per_sqft).replace('$', '')}</div>
            <span className="cap">Base price</span>
          </PriceBox>
        </Hero>

        {/* Where this service is actually used — the page previously showed
            nothing about this, even though deleting depends on it. */}
        <Stats>
          <Stat>
            <div className="n">{usage.estimateItems ?? '—'}</div>
            <div className="l"><FaFileInvoiceDollar size={11} /> Estimate line items</div>
          </Stat>
          <Stat>
            <div className="n">{usage.leadRequests ?? '—'}</div>
            <div className="l"><FaUserTag size={11} /> Lead requests</div>
          </Stat>
          <Stat>
            <div className="n">{usage.changeOrderItems ?? '—'}</div>
            <div className="l"><FaClipboardList size={11} /> Change-order items</div>
          </Stat>
        </Stats>

        <Section>
          <h2><FaFileAlt /> Description</h2>
          {service.description?.trim()
            ? <p>{service.description}</p>
            : <p className="empty">No description written for this service yet.</p>}
        </Section>

        {service.template?.trim() && (
          <Section>
            <h2><FaFileAlt /> Estimate template</h2>
            <p>{service.template}</p>
          </Section>
        )}

        <Meta>
          <span><FaCalendarAlt size={10} /> Created {shortDate(service.createdAt)}</span>
          <span><FaCalendarAlt size={10} /> Updated {shortDate(service.updatedAt)}</span>
          <span>ID #{service.id}</span>
        </Meta>
      </Page>

      <Modal
        isOpen={deleteModalOpen}
        title="Delete Service"
        message={inUse
          ? `This service is used by ${usage.estimateItems || 0} estimate item(s) and ${usage.leadRequests || 0} lead request(s), so it will be archived rather than deleted. Continue?`
          : 'Are you sure you want to delete this service? If it\'s used by existing leads or estimates, it will be archived instead.'}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={() => {
          setShowFeedback(false);
          if (redirectAfterFeedback) navigate('/services');
        }}
      />
    </DashboardLayout>
  );
}
